import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Lock,
  Unlock,
  Copy,
  Check,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Scale,
  Eye,
  EyeOff,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react';

/**
 * HP.4 Gated Output: Career Decision Display
 * Shows visible career alignment factors + gated deep analysis
 */

const ALIGNMENT_ICONS = {
  positive: ArrowUpRight,
  negative: ArrowDownRight,
  neutral: Minus,
};

const GatedCareerDisplay = ({ data, onUnlock, onStartFresh, accentColor = '#06b6d4' }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  if (!data) return null;

  const {
    review_id,
    input_hash,
    summary,
    achievements = [],
    development_areas = {},
    goals = {},
  } = data;

  const visibleItems = achievements.filter(a => a.visible);
  const gatedItems = achievements.filter(a => !a.visible);
  const gatedDevAreas = development_areas.gated_count || 0;
  const gatedGoals = goals.gated_count || 0;
  const totalGated = gatedItems.length + gatedDevAreas + gatedGoals;

  const toggleItem = (index) => {
    setExpandedItems(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleCopy = () => {
    const text = [
      'CAREER DECISION ANALYSIS',
      '',
      summary,
      '',
      'KEY FACTORS:',
      ...visibleItems.map((c, i) => `${i + 1}. ${c.title}\n   ${c.detail}`),
    ].join('\n');

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleUnlock = () => {
    const unlockData = { review_id, input_hash, flow_type: 'career' };
    sessionStorage.setItem('rv_pending_unlock', JSON.stringify(unlockData));
    navigate('/login');
  };

  return (
    <div data-testid="career-gated-display" className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accentColor}20` }}>
            <Scale className="w-4 h-4" style={{ color: accentColor }} />
          </div>
          <h2 className="text-xl font-bold text-white">Your Career Analysis</h2>
        </div>
        <Button data-testid="career-copy-btn" variant="ghost" size="sm" onClick={handleCopy} className="text-gray-400 hover:text-white">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          <span className="ml-1.5 text-xs">{copied ? 'Copied' : 'Copy'}</span>
        </Button>
      </div>

      {/* Summary */}
      <div data-testid="career-summary" className="p-5 rounded-xl border mb-6" style={{ borderColor: `${accentColor}30`, backgroundColor: `${accentColor}08` }}>
        <p className="text-gray-200 leading-relaxed text-sm">{summary}</p>
      </div>

      {/* Visible Factors */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="w-4 h-4" style={{ color: accentColor }} />
          <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Key Career Factors</h3>
        </div>
        <div className="space-y-3">
          {visibleItems.map((item, i) => {
            const isExpanded = expandedItems[i];
            return (
              <div
                key={i}
                data-testid={`career-factor-result-${i}`}
                className="p-4 bg-gray-800/40 border border-gray-700/50 rounded-lg cursor-pointer hover:border-gray-600 transition-colors"
                onClick={() => toggleItem(i)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center mt-0.5" style={{ backgroundColor: `${accentColor}15` }}>
                      <Scale className="w-4 h-4" style={{ color: accentColor }} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium text-sm">{item.title}</h4>
                      {isExpanded && <p className="text-gray-400 text-sm mt-2 leading-relaxed">{item.detail}</p>}
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500 mt-1" /> : <ChevronDown className="w-4 h-4 text-gray-500 mt-1" />}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Gated Section */}
      {totalGated > 0 && (
        <div data-testid="career-gated-section" className="relative p-6 rounded-xl border border-dashed mb-6" style={{ borderColor: `${accentColor}40` }}>
          <div className="absolute inset-0 rounded-xl backdrop-blur-sm bg-gray-900/60 z-10 flex flex-col items-center justify-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${accentColor}20` }}>
              <Lock className="w-5 h-5" style={{ color: accentColor }} />
            </div>
            <p className="text-white font-semibold text-sm mb-1">
              {totalGated} more insight{totalGated !== 1 ? 's' : ''} available
            </p>
            <p className="text-gray-400 text-xs mb-4 text-center max-w-xs">
              Full gap analysis, risk assessment, decision framework, and next steps
            </p>
            <Button data-testid="career-unlock-btn" onClick={handleUnlock} className="text-white font-medium px-6" style={{ backgroundColor: accentColor }}>
              <Unlock className="w-4 h-4 mr-2" /> Unlock Full Analysis — Free
            </Button>
          </div>
          <div className="opacity-30 select-none">
            <div className="flex items-center gap-2 mb-3">
              <EyeOff className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Deeper Analysis</h3>
            </div>
            <div className="space-y-3">
              {[...Array(Math.max(totalGated, 2))].map((_, i) => (
                <div key={i} className="p-3 bg-gray-800/30 rounded-lg">
                  <div className="h-4 bg-gray-700/50 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-700/30 rounded w-full" />
                  <div className="h-3 bg-gray-700/30 rounded w-5/6 mt-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-800">
        <Button data-testid="career-start-fresh-btn" variant="ghost" size="sm" onClick={onStartFresh} className="text-gray-400 hover:text-white">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Start Fresh
        </Button>
        <p className="text-xs text-gray-500">Generated by AI — your data is not stored unless you sign up</p>
      </div>
    </div>
  );
};

export default GatedCareerDisplay;
